import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import axios from 'axios';
import { getServerPrefix } from '@/utils';
import type { Locale } from '@/i18n/models';

export const defaultConfig: HttpOptions = {
  baseURL: getServerPrefix(window.location),
  headers: {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    'Content-Type': 'application/json'
  }
};

export class Http implements HttpService {
  private client: AxiosInstance;

  constructor({ baseURL, headers }: HttpOptions = defaultConfig) {
    this.client = axios.create({ baseURL, headers });
  }

  get<T = any, R = Response<T>>(url: string, config?: RequestConfig): Promise<R> {
    return this.client.get(url, config);
  }

  post<T = any, R = Response<T>>(url: string, data?: any, config?: RequestConfig): Promise<R> {
    return this.client.post(url, data, config);
  }

  put<T = any, R = Response<T>>(url: string, data?: any, config?: RequestConfig): Promise<R> {
    return this.client.put(url, data, config);
  }

  delete<T = any, R = Response<T>>(url: string, config?: RequestConfig): Promise<R> {
    return this.client.delete(url, config);
  }

  setLanguageHeader(locale: Locale): void {
    this.setCommonHeader('Accept-Language', [locale].filter((v: string) => Boolean(v)).join(';'));
  }

  setCommonHeader(name: string, value: any): void {
    // eslint-disable-next-line functional/immutable-data
    this.client.defaults.headers.common = {
      ...this.client.defaults.headers.common,
      [name]: value
    };
  }
}

export interface HttpService {
  get<T = any, R = Response<T>>(url: string, config?: RequestConfig): Promise<R>;

  post<T = any, R = Response<T>>(url: string, data?: any, config?: RequestConfig): Promise<R>;

  put<T = any, R = Response<T>>(url: string, data?: any, config?: RequestConfig): Promise<R>;

  delete<T = any, R = Response<T>>(url: string, config?: RequestConfig): Promise<R>;

  setCommonHeader(name: string, value: any): void;

  setLanguageHeader(locale: Locale): void;
}

export interface HttpOptions {
  readonly baseURL?: string;
  readonly headers?: { readonly [key: string]: string };
}

export type RequestConfig = AxiosRequestConfig;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export type Response<T = any> = AxiosResponse;
